import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, Typography, Card, CardContent, Grid, Stack, 
  CircularProgress, Alert, ToggleButton, ToggleButtonGroup, Table,
  TableBody, TableCell, TableHead, TableRow, IconButton, Tooltip
} from '@mui/material';
import { 
  EmojiEvents as TrophyIcon,
  Whatshot as FireIcon,
  TrendingUp as TrendingUpIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LinkedIn as LinkedInIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import logger from './../utils/logger.js';

const STATS_REFRESH_MS = 5 * 60 * 1000;

const MEMBER_COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042',
  '#8884d8', '#82ca9d', '#ffc658', '#a4de6c',
  '#d0ed57', '#83a6ed', '#8dd1e1', '#FF6E5A',
];

const memberColor = (username) => {
  let hash = 0;
  const name = username || '';
  for (let i = 0; i < name.length; i += 1) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return MEMBER_COLORS[Math.abs(hash) % MEMBER_COLORS.length];
};

const TeamShareBar = ({ members, metric }) => {
  const parts = members.map((member) => ({
    username: member.username,
    displayName: member.displayName || member.username,
    value: Number(member.stats?.[metric]) || 0,
    color: memberColor(member.username),
  }));
  const total = parts.reduce((sum, part) => sum + part.value, 0);

  return (
    <Box sx={{ mt: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          height: 8,
          width: '100%',
          borderRadius: 4,
          overflow: 'hidden',
          bgcolor: 'action.hover',
        }}
      >
        {total > 0 && parts.filter((part) => part.value > 0).map((part) => (
          <Tooltip
            key={part.username}
            title={`${part.displayName}: ${part.value} (${Math.round((part.value / total) * 100)}%)`}
          >
            <Box sx={{ flexGrow: part.value, flexBasis: 0, bgcolor: part.color, height: '100%' }} />
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
};

const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This week' },
  { id: 'month', label: 'This month' },
  { id: 'lastMonth', label: 'Last month' },
];

export const outreachPeriodBounds = (period, now = new Date()) => {
  if (period === 'week') {
    return {
      start: format(startOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
      end: format(endOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
    };
  }
  if (period === 'month') {
    return {
      start: format(startOfMonth(now), 'yyyy-MM-dd'),
      end: format(endOfMonth(now), 'yyyy-MM-dd'),
    };
  }
  if (period === 'lastMonth') {
    const previous = subMonths(now, 1);
    return {
      start: format(startOfMonth(previous), 'yyyy-MM-dd'),
      end: format(endOfMonth(previous), 'yyyy-MM-dd'),
    };
  }
  const today = format(now, 'yyyy-MM-dd');
  return { start: today, end: today };
};

const OutreachStats = ({ refreshKey = 0 }) => {
  const { user, team } = useAuth();
  
  const [statistics, setStatistics] = useState(null);
  const [teammateStats, setTeammateStats] = useState([]);
  const [period, setPeriod] = useState('today');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const hasStats = useRef(false);

  const teammateKey = (team?.members || [])
    .map((member) => member.username)
    .filter(Boolean)
    .join(',');
  
  const loadStatistics = useCallback(async () => {
    if (!user?.username) return;
    const teammates = (team?.members || []).filter(
      (member) => member?.username && member.username !== user.username
    );
    const firstLoad = !hasStats.current;
    try {
      if (firstLoad) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError(null);
      const { start, end } = outreachPeriodBounds(period);
      const response = await api.getUserOutreachStatistics(user.username, start, end);
      if (response.success) {
        setStatistics(response.data || null);
        hasStats.current = true;
      } else if (firstLoad) {
        setError(response.message || 'Failed to load statistics');
      }

      if (teammates.length > 0) {
        const rows = await Promise.all(
          teammates.map(async (member) => {
            try {
              const memberResponse = await api.getUserOutreachStatistics(member.username, start, end);
              return {
                username: member.username,
                displayName: member.display_name || member.username,
                stats: memberResponse.success ? memberResponse.data : null,
              };
            } catch (err) {
              logger.error(`Error loading stats for ${member.username}:`, err);
              return {
                username: member.username,
                displayName: member.display_name || member.username,
                stats: null,
              };
            }
          })
        );
        setTeammateStats(rows);
      } else {
        setTeammateStats([]);
      }
    } catch (err) {
      logger.error('Error loading statistics:', err);
      if (firstLoad) {
        setError('Failed to load statistics');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user?.username, period, team, teammateKey]);
  
  useEffect(() => {
    loadStatistics();
  }, [loadStatistics, refreshKey, team?.id]);

  useEffect(() => {
    if (!user?.show_outreach_performance) return undefined;
    const timer = setInterval(() => {
      loadStatistics();
    }, STATS_REFRESH_MS);
    return () => clearInterval(timer);
  }, [user?.show_outreach_performance, loadStatistics]);
  
  const getMotivationalMessage = () => {
    if (!statistics) return "";
    
    if (statistics.total_outreaches === 0) {
      return "Ready to start your outreach journey! Your first contact could lead to your next big win!";
    }
    
    if (statistics.meetings_scheduled > 0) {
      return `Amazing work! You've scheduled ${statistics.meetings_scheduled} meeting${statistics.meetings_scheduled > 1 ? 's' : ''}. Keep the momentum going!`;
    }
    
    if (statistics.successful_contacts > 3) {
      return "You're on a roll! Your persistence is paying off with great contact rates!";
    }
    
    if (statistics.successful_contacts > 0) {
      return "Great job making successful contacts! Each conversation brings you closer to your goals.";
    }
    
    return "Every outreach is progress! Stay positive - your next contact could be the breakthrough!";
  };
  
  const getStreakMessage = () => {
    if (!statistics || !statistics.current_streak) return "";
    
    if (statistics.current_streak >= 5) {
      return `🔥 ${statistics.current_streak} day outreach streak! You're unstoppable!`;
    } else if (statistics.current_streak >= 3) {
      return `🔥 ${statistics.current_streak} day streak! Building momentum!`;
    } else if (statistics.current_streak > 0) {
      return `You've reached out ${statistics.current_streak} day${statistics.current_streak > 1 ? 's' : ''} in a row!`;
    }
    
    return "";
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }
  
  if (!statistics || !user) {
    return null;
  }
  
  if (!user.show_outreach_performance) {
    return null;
  }

  const shareMembers = teammateStats.length > 0
    ? [
        {
          username: user.username,
          displayName: user.display_name || user.username,
          stats: statistics,
        },
        ...teammateStats,
      ]
    : [];
  
  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
          <TrophyIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle', color: 'gold' }} />
          Your Outreach Performance
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {statistics.current_streak > 0 && (
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: 'success.main', 
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <FireIcon color="error" sx={{ mr: 0.5 }} />
              {getStreakMessage()}
            </Typography>
          )}
          <Tooltip title="Refresh stats">
            <span>
              <IconButton
                aria-label="Refresh stats"
                onClick={() => loadStatistics()}
                disabled={refreshing}
                size="small"
              >
                {refreshing ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={period}
            onChange={(event, value) => {
              if (value) setPeriod(value);
            }}
            aria-label="Outreach period"
          >
            {PERIODS.map((option) => (
              <ToggleButton key={option.id} value={option.id}>
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>
      
      <Typography 
        variant="subtitle1" 
        color="primary" 
        sx={{ mb: 2, fontWeight: 'medium' }}
      >
        {getMotivationalMessage()}
      </Typography>
      
      {shareMembers.length > 0 && (
        <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
          {shareMembers.map((member) => (
            <Stack key={member.username} direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: memberColor(member.username) }} />
              <Typography variant="caption" color="text.secondary">
                {member.username === user.username ? 'You' : member.displayName}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            variant="outlined" 
            sx={{ 
              bgcolor: 'background.paper', 
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: 2
              }
            }}
          >
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Your Outreaches</Typography>
              <Typography variant="h4" color="primary.main">{statistics.total_outreaches}</Typography>
              {statistics.total_outreaches > 0 && (
                <Typography variant="caption" color="success.main">
                  <TrendingUpIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Each one counts!
                </Typography>
              )}
              {shareMembers.length > 0 && (
                <TeamShareBar members={shareMembers} metric="total_outreaches" />
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            variant="outlined" 
            sx={{ 
              bgcolor: 'background.paper', 
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: 2
              }
            }}
          >
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Successful Contacts</Typography>
              <Typography variant="h4" color="success.main">{statistics.successful_contacts}</Typography>
              <Typography variant="caption" color="success.main">
                {statistics.success_rate > 30 ? "Amazing" : "Good"} {statistics.success_rate}% success rate!
              </Typography>
              {shareMembers.length > 0 && (
                <TeamShareBar members={shareMembers} metric="successful_contacts" />
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            variant="outlined" 
            sx={{ 
              bgcolor: 'background.paper', 
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: 2
              }
            }}
          >
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Meetings Scheduled</Typography>
              <Typography variant="h4" sx={{ color: '#ff9800' }}>{statistics.meetings_scheduled}</Typography>
              <Typography variant="caption" color="success.main">
                {statistics.meeting_conversion_rate > 0 ? `Impressive ${statistics.meeting_conversion_rate}% conversion!` : "Your next call could land a meeting!"}
              </Typography>
              {shareMembers.length > 0 && (
                <TeamShareBar members={shareMembers} metric="meetings_scheduled" />
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card 
            variant="outlined" 
            sx={{ 
              bgcolor: 'background.paper', 
              transition: 'all 0.3s',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: 2
              }
            }}
          >
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Your Channel Success</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <PhoneIcon fontSize="small" color="primary" />
                <Typography>{statistics.by_method?.phone || 0}</Typography>
                <EmailIcon fontSize="small" color="info" />
                <Typography>{statistics.by_method?.email || 0}</Typography>
                <LinkedInIcon fontSize="small" color="primary" />
                <Typography>{statistics.by_method?.linkedin || 0}</Typography>
              </Stack>
              <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                {(statistics.by_method?.phone > statistics.by_method?.email && 
                  statistics.by_method?.phone > statistics.by_method?.linkedin) 
                  ? "Phone calls are your strength!" 
                  : (statistics.by_method?.email > statistics.by_method?.linkedin) 
                    ? "Emails are working well for you!" 
                    : "LinkedIn is your best channel!"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {teammateStats.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Team</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Teammate</TableCell>
                <TableCell align="right">Outreaches</TableCell>
                <TableCell align="right">Successful contacts</TableCell>
                <TableCell align="right">Meetings</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {teammateStats.map((row) => (
                <TableRow key={row.username}>
                  <TableCell>{row.displayName}</TableCell>
                  <TableCell align="right">{row.stats?.total_outreaches ?? '—'}</TableCell>
                  <TableCell align="right">{row.stats?.successful_contacts ?? '—'}</TableCell>
                  <TableCell align="right">{row.stats?.meetings_scheduled ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default OutreachStats;
